var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));

// src/HistoryQueueContext.tsx
import { createContext, useContext, useMemo } from "react";
import { jsx } from "react/jsx-runtime";
var HistoryQueueContext = createContext({
  requestHistoryTick: () => {
  }
});
var HistoryQueueProvider = ({
  children,
  requestHistoryTick
}) => {
  const ctx = useMemo(
    () => ({
      requestHistoryTick
    }),
    [requestHistoryTick]
  );
  return /* @__PURE__ */ jsx(HistoryQueueContext.Provider, { value: ctx, children });
};
var useHistoryTick = () => useContext(HistoryQueueContext);

// src/historySyncPlugin.tsx
import { id, makeEvent } from "@stackflow/core";
import { createBrowserHistory, createMemoryHistory } from "history";

// src/RoutesContext.tsx
import { createContext as createContext2, useContext as useContext2 } from "react";
import { jsx as jsx2 } from "react/jsx-runtime";
var RoutesContext = createContext2([]);
var RoutesProvider = (props) => /* @__PURE__ */ jsx2(RoutesContext.Provider, { value: props.routes, children: props.children });
RoutesProvider.displayName = "RoutesProvider";
function useRoutes() {
  return useContext2(RoutesContext);
}

// src/historyState.ts
import { parse, stringify } from "flatted";
var STATE_TAG = "@stackflow/plugin-history-sync";
function serializeState(state) {
  return {
    _TAG: STATE_TAG,
    flattedState: stringify({
      activity: state.activity,
      step: state.step
    })
  };
}
function isSerializedState(input) {
  return typeof input === "object" && input !== null && "_TAG" in input && "flattedState" in input && typeof input._TAG === "string" && input._TAG === STATE_TAG && typeof input.flattedState === "string";
}
function parseState(input) {
  try {
    return isSerializedState(input) ? parse(input.flattedState) : null;
  } catch (e) {
    return null;
  }
}
function pushState({
  history,
  pathname,
  state,
  useHash
}) {
  const nextPathname = useHash ? `${history.location.pathname}#${pathname}` : pathname;
  history.push(nextPathname, serializeState(state));
}
function replaceState({
  history,
  pathname,
  state,
  useHash
}) {
  const nextPathname = useHash ? `${history.location.pathname}#${pathname}` : pathname;
  history.replace(nextPathname, serializeState(state));
}

// src/last.ts
function last(arr) {
  return arr.length === 0 ? void 0 : arr[arr.length - 1];
}

// src/makeHistoryTaskQueue.ts
var makeHistoryTaskQueue = (history) => {
  let previousTask = Promise.resolve();
  const requestHistoryTick = (cb) => {
    previousTask = previousTask.then(
      () => new Promise((resolve) => {
        const clean = history.listen(() => {
          clean();
          resolve();
        });
        cb(() => {
          clean();
          resolve();
        });
      })
    );
  };
  return { requestHistoryTick };
};

// src/makeTemplate.ts
import UrlPattern from "url-pattern";
function pathToUrl(path) {
  return new URL(path, "file://");
}
function urlSearchParamsToMap(urlSearchParams) {
  const map = {};
  urlSearchParams.forEach((value, key) => {
    map[key] = value;
  });
  return map;
}
function appendTrailingSlashInPathname(pathname) {
  if (pathname.endsWith("/")) {
    return pathname;
  }
  return `${pathname}/`;
}
function prependQuestionMarkInSearchParams(searchParams) {
  const searchParamsStr = searchParams.toString();
  if (searchParamsStr.length > 0) {
    return `?${searchParams}`;
  }
  return searchParams;
}
function makeTemplate({ path, decode }, urlPatternOptions) {
  const pattern = new UrlPattern(`${path}(/)`, urlPatternOptions);
  const onlyAsterisk = path === "*" || path === "/*";
  const variableCount = onlyAsterisk ? Number.POSITIVE_INFINITY : pattern.names.length;
  return {
    fill(params) {
      const pathname = pattern.stringify(params);
      const pathParams = pattern.match(pathname);
      const searchParamsMap = __spreadValues({}, params);
      Object.keys(pathParams).forEach((key) => {
        delete searchParamsMap[key];
      });
      const searchParams = new URLSearchParams(
        Object.entries(searchParamsMap).reduce(
          (acc, [key, value]) => __spreadValues(__spreadValues({}, acc), value ? {
            [key]: value
          } : null),
          {}
        )
      );
      return appendTrailingSlashInPathname(pathname) + prependQuestionMarkInSearchParams(searchParams);
    },
    parse(path2) {
      const url = pathToUrl(path2);
      const pathParams = pattern.match(url.pathname);
      const searchParams = urlSearchParamsToMap(url.searchParams);
      if (!pathParams) {
        return null;
      }
      const params = __spreadValues(__spreadValues({}, searchParams), pathParams);
      return decode ? decode(params) : params;
    },
    variableCount
  };
}

// src/normalizeRouteInput.ts
function makeRoute(path) {
  return typeof path === "string" ? { path } : path;
}
function normalizeRouteInput(route) {
  return (Array.isArray(route) ? route : [route]).map(makeRoute);
}

// src/normalizeActivityRouteMap.ts
function normalizeActivityRouteMap(activityRouteMap) {
  const routes = Object.keys(activityRouteMap).flatMap((activityName) => {
    const routeInput = activityRouteMap[activityName];
    if (!routeInput) {
      return [];
    }
    return normalizeRouteInput(routeInput).map((route) => __spreadValues({
      activityName
    }, route));
  });
  return routes;
}

// src/sortActivityRoutes.ts
var paramRe = /^:[\w-]+$/;
var dynamicSegmentValue = 3;
var emptySegmentValue = 1;
var staticSegmentValue = 10;
var splatPenalty = -2;
var isSplat = (s) => s === "*";
function computeScore(path) {
  const segments = path.split("/");
  let initialScore = segments.length;
  if (segments.some(isSplat)) {
    initialScore += splatPenalty;
  }
  return segments.filter((s) => !isSplat(s)).reduce(
    (score, segment) => score + (paramRe.test(segment) ? dynamicSegmentValue : segment === "" ? emptySegmentValue : staticSegmentValue),
    initialScore
  );
}
function sortActivityRoutes(routes) {
  return [...routes].sort(
    (a, b) => computeScore(b.path) - computeScore(a.path)
  );
}

// src/historySyncPlugin.tsx
import { jsx as jsx3 } from "react/jsx-runtime";
var SECOND = 1e3;
var MINUTE = 60 * SECOND;
function historySyncPlugin(options) {
  var _a;
  if ("config" in options) {
    options.config.decorate("historySync", {
      makeTemplate,
      urlPatternOptions: options.urlPatternOptions
    });
  }
  const history = (_a = options.history) != null ? _a : typeof window === "undefined" ? createMemoryHistory({}) : createBrowserHistory({
    window
  });
  const { location } = history;
  const routes = "routes" in options ? options.routes : options.config.activities.reduce(
    (acc, a) => __spreadProps(__spreadValues({}, acc), { [a.name]: a.route }),
    {}
  );
  const activityRoutes = sortActivityRoutes(normalizeActivityRouteMap(routes));
  return () => {
    let pushFlag = 0;
    let silentFlag = false;
    const { requestHistoryTick } = makeHistoryTaskQueue(history);
    return {
      key: "plugin-history-sync",
      wrapStack({ stack }) {
        return /* @__PURE__ */ jsx3(HistoryQueueProvider, { requestHistoryTick, children: /* @__PURE__ */ jsx3(RoutesProvider, { routes: activityRoutes, children: stack.render() }) });
      },
      overrideInitialEvents({ initialContext }) {
        var _a2, _b;
        const initialState = parseState(history.location.state);
        if (initialState) {
          return [
            __spreadProps(__spreadValues({}, initialState.activity.enteredBy), {
              name: "Pushed"
            }),
            ...((_a2 = initialState.step) == null ? void 0 : _a2.enteredBy.name) === "StepPushed" || ((_b = initialState.step) == null ? void 0 : _b.enteredBy.name) === "StepReplaced" ? [
              __spreadProps(__spreadValues({}, initialState.step.enteredBy), {
                name: "StepPushed"
              })
            ] : []
          ];
        }
        function resolveCurrentPath() {
          var _a3, _b2;
          if (((_a3 = initialContext == null ? void 0 : initialContext.req) == null ? void 0 : _a3.path) && typeof initialContext.req.path === "string") {
            return initialContext.req.path;
          }
          if (options.useHash) {
            return (_b2 = location.hash.split("#")[1]) != null ? _b2 : "/";
          }
          return location.pathname + location.search;
        }
        const currentPath = resolveCurrentPath();
        if (currentPath) {
          for (const activityRoute of activityRoutes) {
            const template = makeTemplate(
              activityRoute,
              options.urlPatternOptions
            );
            const activityParams = template.parse(currentPath);
            if (activityParams) {
              const activityId = id();
              return [
                makeEvent("Pushed", {
                  activityId,
                  activityName: activityRoute.activityName,
                  activityParams: __spreadValues({}, activityParams),
                  eventDate: (/* @__PURE__ */ new Date()).getTime() - MINUTE,
                  activityContext: {
                    path: currentPath
                  }
                })
              ];
            }
          }
        }
        const fallbackActivityId = id();
        const fallbackActivityName = options.fallbackActivity({
          initialContext
        });
        const fallbackActivityRoute = activityRoutes.find(
          (r) => r.activityName === fallbackActivityName
        );
        const fallbackActivityPath = fallbackActivityRoute == null ? void 0 : fallbackActivityRoute.path;
        const fallbackActivityParams = urlSearchParamsToMap(
          pathToUrl(currentPath).searchParams
        );
        return [
          makeEvent("Pushed", {
            activityId: fallbackActivityId,
            activityName: fallbackActivityName,
            activityParams: __spreadValues({}, fallbackActivityParams),
            eventDate: (/* @__PURE__ */ new Date()).getTime() - MINUTE,
            activityContext: {
              path: fallbackActivityPath
            }
          })
        ];
      },
      onInit({ actions: { getStack, dispatchEvent, push, stepPush } }) {
        const rootActivity = getStack().activities[0];
        const match = activityRoutes.find(
          (r) => r.activityName === rootActivity.name
        );
        const template = makeTemplate(match, options.urlPatternOptions);
        const lastStep = last(rootActivity.steps);
        requestHistoryTick(
          () => replaceState({
            history,
            pathname: template.fill(rootActivity.params),
            state: {
              activity: rootActivity,
              step: lastStep
            },
            useHash: options.useHash
          })
        );
        const onPopState = (e) => {
          if (silentFlag) {
            silentFlag = false;
            return;
          }
          const state = parseState(e.location.state);
          if (!state) {
            return;
          }
          const targetActivity = state.activity;
          const targetActivityId = state.activity.id;
          const targetStep = state.step;
          const { activities } = getStack();
          const currentActivity = activities.find(
            (activity) => activity.isActive
          );
          if (!currentActivity) {
            return;
          }
          const currentStep = last(currentActivity.steps);
          const nextActivity = activities.find(
            (activity) => activity.id === targetActivityId
          );
          const nextStep = currentActivity.steps.find(
            (step) => step.id === (targetStep == null ? void 0 : targetStep.id)
          );
          const isBackward = () => currentActivity.id > targetActivityId;
          const isForward = () => currentActivity.id < targetActivityId;
          const isStep = () => currentActivity.id === targetActivityId;
          const isStepBackward = () => {
            if (!isStep()) {
              return false;
            }
            if (!targetStep) {
              return true;
            }
            if (currentStep && currentStep.id > targetStep.id) {
              return true;
            }
            return false;
          };
          const isStepForward = () => {
            if (!isStep()) {
              return false;
            }
            if (!currentStep) {
              return true;
            }
            if (targetStep && currentStep.id < targetStep.id) {
              return true;
            }
            return false;
          };
          if (isBackward()) {
            dispatchEvent("Popped", {});
            if (!nextActivity) {
              pushFlag += 1;
              push(__spreadValues({}, targetActivity.enteredBy));
              if ((targetStep == null ? void 0 : targetStep.enteredBy.name) === "StepPushed" || (targetStep == null ? void 0 : targetStep.enteredBy.name) === "StepReplaced") {
                const { enteredBy } = targetStep;
                pushFlag += 1;
                stepPush(__spreadValues({}, enteredBy));
              }
            }
          }
          if (isStepBackward()) {
            if (!nextStep && targetStep && ((targetStep == null ? void 0 : targetStep.enteredBy.name) === "StepPushed" || (targetStep == null ? void 0 : targetStep.enteredBy.name) === "StepReplaced")) {
              const { enteredBy } = targetStep;
              pushFlag += 1;
              stepPush(__spreadValues({}, enteredBy));
            }
            dispatchEvent("StepPopped", {});
          }
          if (isForward()) {
            pushFlag += 1;
            push({
              activityId: targetActivity.id,
              activityName: targetActivity.name,
              activityParams: targetActivity.params
            });
          }
          if (isStepForward()) {
            if (!targetStep) {
              return;
            }
            pushFlag += 1;
            stepPush({
              stepId: targetStep.id,
              stepParams: targetStep.params
            });
          }
        };
        history.listen(onPopState);
      },
      onPushed({ effect: { activity } }) {
        if (pushFlag) {
          pushFlag -= 1;
          return;
        }
        const match = activityRoutes.find(
          (r) => r.activityName === activity.name
        );
        const template = makeTemplate(match, options.urlPatternOptions);
        requestHistoryTick(() => {
          silentFlag = true;
          pushState({
            history,
            pathname: template.fill(activity.params),
            state: {
              activity
            },
            useHash: options.useHash
          });
        });
      },
      onStepPushed({ effect: { activity, step } }) {
        if (pushFlag) {
          pushFlag -= 1;
          return;
        }
        const match = activityRoutes.find(
          (r) => r.activityName === activity.name
        );
        const template = makeTemplate(match, options.urlPatternOptions);
        requestHistoryTick(() => {
          silentFlag = true;
          pushState({
            history,
            pathname: template.fill(activity.params),
            state: {
              activity,
              step
            },
            useHash: options.useHash
          });
        });
      },
      onReplaced({ effect: { activity } }) {
        if (!activity.isActive) {
          return;
        }
        const match = activityRoutes.find(
          (r) => r.activityName === activity.name
        );
        const template = makeTemplate(match, options.urlPatternOptions);
        requestHistoryTick(() => {
          silentFlag = true;
          replaceState({
            history,
            pathname: template.fill(activity.params),
            state: {
              activity
            },
            useHash: options.useHash
          });
        });
      },
      onStepReplaced({ effect: { activity, step } }) {
        if (!activity.isActive) {
          return;
        }
        const match = activityRoutes.find(
          (r) => r.activityName === activity.name
        );
        const template = makeTemplate(match, options.urlPatternOptions);
        requestHistoryTick(() => {
          silentFlag = true;
          replaceState({
            history,
            pathname: template.fill(activity.params),
            state: {
              activity,
              step
            },
            useHash: options.useHash
          });
        });
      },
      onBeforePush({ actionParams, actions: { overrideActionParams } }) {
        const match = activityRoutes.find(
          (r) => r.activityName === actionParams.activityName
        );
        const template = makeTemplate(match, options.urlPatternOptions);
        const path = template.fill(actionParams.activityParams);
        overrideActionParams(__spreadProps(__spreadValues({}, actionParams), {
          activityContext: __spreadProps(__spreadValues({}, actionParams.activityContext), {
            path
          })
        }));
      },
      onBeforeReplace({
        actionParams,
        actions: { overrideActionParams, getStack }
      }) {
        const match = activityRoutes.find(
          (r) => r.activityName === actionParams.activityName
        );
        const template = makeTemplate(match, options.urlPatternOptions);
        const path = template.fill(actionParams.activityParams);
        overrideActionParams(__spreadProps(__spreadValues({}, actionParams), {
          activityContext: __spreadProps(__spreadValues({}, actionParams.activityContext), {
            path
          })
        }));
        const { activities } = getStack();
        const enteredActivities = activities.filter(
          (currentActivity) => currentActivity.transitionState === "enter-active" || currentActivity.transitionState === "enter-done"
        );
        const previousActivity = enteredActivities.length > 0 ? enteredActivities[enteredActivities.length - 1] : null;
        if (previousActivity) {
          for (let i = 0; i < previousActivity.steps.length - 1; i += 1) {
            requestHistoryTick((resolve) => {
              if (!parseState(history.location.state)) {
                silentFlag = true;
                history.back();
              } else {
                resolve();
              }
            });
            requestHistoryTick(() => {
              silentFlag = true;
              history.back();
            });
          }
        }
      },
      onBeforeStepPop({ actions: { getStack } }) {
        var _a2;
        const { activities } = getStack();
        const currentActivity = activities.find(
          (activity) => activity.isActive
        );
        if (((_a2 = currentActivity == null ? void 0 : currentActivity.steps.length) != null ? _a2 : 0) > 1) {
          requestHistoryTick(() => {
            silentFlag = true;
            history.back();
          });
        }
      },
      onBeforePop({ actions: { getStack } }) {
        const { activities } = getStack();
        const currentActivity = activities.find(
          (activity) => activity.isActive
        );
        if (currentActivity) {
          const { isRoot, steps } = currentActivity;
          const popCount = isRoot ? 0 : steps.length;
          for (let i = 0; i < popCount; i += 1) {
            requestHistoryTick((resolve) => {
              if (!parseState(history.location.state)) {
                silentFlag = true;
                history.back();
              } else {
                resolve();
              }
            });
            requestHistoryTick(() => {
              silentFlag = true;
              history.back();
            });
          }
        }
      }
    };
  };
}
export {
  historySyncPlugin,
  makeTemplate,
  useHistoryTick,
  useRoutes
};
//# sourceMappingURL=index.mjs.map
